import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Wallet as WalletIcon, CreditCard, Clock, TrendingUp, Zap, AlertCircle, Loader2, CheckCircle, RefreshCw
} from 'lucide-react';
import ErrorBoundary from '../ErrorBoundary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGES = {
  starter: { id: 'starter', name: 'Starter', credits: 10, price: 'R100', perCredit: 'R10' },
  growth:  { id: 'growth',  name: 'Growth',  credits: 50, price: 'R450', perCredit: 'R9'  },
  pro:     { id: 'pro',     name: 'Pro',     credits: 200, price: 'R1500', perCredit: 'R7.50' },
};

const WalletInner = () => {
  const { proId } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [purchasing, setPurchasing] = useState(null); // package_id while loading
  const [paymentStatus, setPaymentStatus] = useState(null); // {type, message}
  const [fetchError, setFetchError] = useState('');

  const fetchData = useCallback(async (silent = false) => {
    if (!proId) {
      setFetchError('Missing professional ID. Please return to your dashboard.');
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    setFetchError('');
    try {
      const [balanceRes, txnsRes] = await Promise.all([
        axios.get(`${API}/credits/balance/${proId}`),
        axios.get(`${API}/credits/transactions/${proId}?limit=50`),
      ]);
      // eslint-disable-next-line no-console
      console.log('[Wallet] balance:', balanceRes.data);
      // eslint-disable-next-line no-console
      console.log('[Wallet] transactions:', txnsRes.data);
      setBalance(balanceRes.data || null);
      setTransactions(Array.isArray(txnsRes.data) ? txnsRes.data : []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Wallet] fetch error:', e?.response?.status, e?.response?.data, e?.message);
      setFetchError(
        e?.response?.status === 404
          ? 'Professional account not found.'
          : 'Could not load wallet. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [proId]);

  // Initial load
  useEffect(() => { fetchData(); }, [fetchData]);

  // Stripe checkout return: poll payment status
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;
    setPaymentStatus({ type: 'pending', message: 'Verifying your payment...' });

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
        const data = res?.data || {};
        // eslint-disable-next-line no-console
        console.log('[Wallet] checkout-status:', data);
        const status = data.payment_status || data.status;
        if (cancelled) return;

        if (status === 'paid' || status === 'complete' || status === 'succeeded') {
          setPaymentStatus({ type: 'success', message: 'Payment successful! Credits added to your wallet.' });
          setSearchParams({}, { replace: true });
          fetchData(true);
          return;
        }
        if (status === 'expired' || status === 'failed' || status === 'canceled' || status === 'cancelled') {
          setPaymentStatus({ type: 'error', message: 'Payment was not completed. Please try again.' });
          setSearchParams({}, { replace: true });
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setPaymentStatus({ type: 'error', message: 'Payment status check timed out. Please refresh in a moment.' });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Wallet] checkout-status error:', e);
        if (!cancelled) {
          setPaymentStatus({ type: 'error', message: 'Could not verify payment status.' });
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, fetchData, setSearchParams]);

  const handlePurchaseCredits = async (packageId) => {
    if (!proId) return;
    setPurchasing(packageId);
    setPaymentStatus(null);
    try {
      const res = await axios.post(`${API}/payments/create-checkout-session`, {
        package_id: packageId,
        professional_id: proId,
        origin_url: window.location.origin,
      });
      const url = res?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setPaymentStatus({ type: 'error', message: 'Could not start checkout. Please try again.' });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Wallet] create-checkout-session error:', e?.response?.data, e?.message);
      setPaymentStatus({
        type: 'error',
        message: e?.response?.data?.detail || 'Could not start checkout. Please try again.',
      });
    } finally {
      setPurchasing(null);
    }
  };

  const getFreeCreditsStatus = () => {
    if (!balance?.free_credits_expiry) return null;
    const expiry = new Date(balance.free_credits_expiry);
    if (Number.isNaN(expiry.getTime())) return null;
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    const expired = expiry < now;
    const usedCount = balance.free_credits_used_count || 0;
    const capped = usedCount >= 5;
    return { expired, daysLeft, capped, usedCount };
  };

  const freeStatus = getFreeCreditsStatus();
  const totalBalance = balance?.credits_balance ?? ((balance?.free_credits_balance || 0) + (balance?.paid_credits_balance || 0));
  const freeBalance = balance?.free_credits_balance ?? 0;
  const paidBalance = balance?.paid_credits_balance ?? 0;
  const safeTransactions = Array.isArray(transactions) ? transactions : [];

  if (loading) {
    return (
      <div data-testid="wallet-loading" className="flex flex-col items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-gray-400 mb-3" />
        <p className="text-sm text-gray-500">Loading your wallet...</p>
      </div>
    );
  }

  if (fetchError && !balance) {
    return (
      <div data-testid="wallet-error-fallback" className="bg-white rounded-xl border border-red-200 p-8 text-center">
        <AlertCircle size={36} className="text-red-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-gray-900 mb-1">Wallet unavailable</h2>
        <p className="text-sm text-gray-500 mb-4">{fetchError}</p>
        <button
          onClick={() => { setRefreshing(true); fetchData(true); }}
          disabled={refreshing}
          data-testid="wallet-retry-btn"
          className="inline-flex items-center gap-2 bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="wallet-page">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wallet</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your credits and view transaction history.</p>
        </div>
        <button
          onClick={() => { setRefreshing(true); fetchData(true); }}
          disabled={refreshing}
          data-testid="wallet-refresh-btn"
          className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Payment Status Alert */}
      {paymentStatus && (
        <div
          data-testid={`payment-status-${paymentStatus.type}`}
          className={`rounded-lg p-4 mb-6 flex items-start gap-3 ${
            paymentStatus.type === 'success' ? 'bg-green-50 border border-green-200' :
            paymentStatus.type === 'error'   ? 'bg-red-50 border border-red-200' :
                                                'bg-blue-50 border border-blue-200'
          }`}
        >
          {paymentStatus.type === 'success' ? (
            <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
          ) : paymentStatus.type === 'pending' ? (
            <Loader2 size={20} className="text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
          ) : (
            <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={`flex-1 text-sm font-medium ${
            paymentStatus.type === 'success' ? 'text-green-900' :
            paymentStatus.type === 'error'   ? 'text-red-900' : 'text-blue-900'
          }`}>
            {paymentStatus.message}
          </p>
          {paymentStatus.type !== 'pending' && (
            <button
              onClick={() => setPaymentStatus(null)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Soft fetch error (non-fatal) */}
      {fetchError && balance && (
        <div className="rounded-lg p-3 mb-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2">
          <AlertCircle size={14} /> {fetchError}
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-[#37b34a] to-[#2d9a3e] rounded-xl p-5 text-white">
          <div className="flex items-center gap-2 mb-2">
            <WalletIcon size={18} />
            <span className="text-sm font-medium opacity-90">Total Balance</span>
          </div>
          <p className="text-3xl font-bold" data-testid="wallet-total-balance">{totalBalance}</p>
          <p className="text-xs opacity-80 mt-1">credits</p>
        </div>

        <div className="bg-white rounded-xl border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={16} className="text-amber-500" />
            <span className="text-sm font-medium text-gray-700">Free Trial Credits</span>
          </div>
          <p className="text-2xl font-bold text-amber-600" data-testid="wallet-free-balance">{freeBalance}</p>
          {freeStatus && !freeStatus.expired && !freeStatus.capped && (
            <p className="text-xs text-gray-500 mt-1">
              {freeStatus.daysLeft} days left · {freeStatus.usedCount}/5 leads used
            </p>
          )}
          {freeStatus?.expired && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle size={10} /> Expired
            </p>
          )}
          {freeStatus?.capped && !freeStatus.expired && (
            <p className="text-xs text-orange-500 mt-1 flex items-center gap-1">
              <AlertCircle size={10} /> Usage limit reached (5/5)
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={16} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Paid Credits</span>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="wallet-paid-balance">{paidBalance}</p>
          <p className="text-xs text-gray-500 mt-1">purchased credits</p>
        </div>
      </div>

      {/* Purchase Credits */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Purchase Credits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Starter */}
          <PackageCard
            pkg={PACKAGES.starter}
            theme="gray"
            ctaLabel="Get Started"
            purchasing={purchasing}
            onPurchase={handlePurchaseCredits}
          />
          {/* Growth */}
          <PackageCard
            pkg={PACKAGES.growth}
            theme="green"
            ctaLabel="Best Value"
            badge="MOST POPULAR"
            saveCopy="Save R50 (10% off)"
            purchasing={purchasing}
            onPurchase={handlePurchaseCredits}
          />
          {/* Pro */}
          <PackageCard
            pkg={PACKAGES.pro}
            theme="purple"
            ctaLabel="Maximum Value"
            badge="25% OFF"
            saveCopy="Save R500"
            purchasing={purchasing}
            onPurchase={handlePurchaseCredits}
          />
        </div>
        <p className="text-xs text-gray-400 mt-4 text-center">
          Secure payment powered by Stripe. Test mode: card 4242 4242 4242 4242
        </p>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Transaction History</h2>
        {safeTransactions.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8" data-testid="wallet-no-transactions">
            No transactions yet.
          </p>
        ) : (
          <div className="space-y-3" data-testid="wallet-transactions-list">
            {safeTransactions.map((txn, idx) => {
              const isDeduction = txn?.type === 'deduction';
              const tsRaw = txn?.timestamp;
              let ts = '';
              if (tsRaw) {
                const d = new Date(tsRaw);
                ts = Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
              }
              return (
                <div key={txn?.id || idx} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isDeduction ? 'bg-red-50' : 'bg-green-50'
                    }`}>
                      <TrendingUp size={14} className={isDeduction ? 'text-red-500 rotate-180' : 'text-green-500'} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{txn?.reference || txn?.type || 'Transaction'}</p>
                      {ts && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock size={10} /> {ts}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className={`text-base font-bold ${isDeduction ? 'text-red-600' : 'text-green-600'}`}>
                    {isDeduction ? '-' : '+'}{txn?.amount ?? 0}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Package card subcomponent
const PackageCard = ({ pkg, theme, ctaLabel, badge, saveCopy, purchasing, onPurchase }) => {
  const isProcessing = purchasing === pkg.id;
  const themes = {
    gray: {
      wrap: 'bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-gray-300',
      btn: 'bg-gray-700 hover:bg-gray-800 text-white',
      title: 'text-gray-600',
      number: 'text-gray-900',
      box: 'bg-white text-gray-900',
      sub: 'text-gray-500',
    },
    green: {
      wrap: 'bg-gradient-to-br from-[#37b34a] to-[#2d9a3e] border-2 border-[#37b34a] shadow-lg text-white',
      btn: 'bg-white text-[#37b34a] hover:bg-gray-100',
      title: 'opacity-90',
      number: 'text-white',
      box: 'bg-white/20 backdrop-blur-sm text-white',
      sub: 'opacity-90',
    },
    purple: {
      wrap: 'bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 hover:border-purple-300',
      btn: 'bg-purple-600 hover:bg-purple-700 text-white',
      title: 'text-purple-600',
      number: 'text-purple-900',
      box: 'bg-white text-purple-900',
      sub: 'text-purple-600',
    },
  };
  const t = themes[theme] || themes.gray;
  return (
    <div
      className={`relative rounded-xl p-5 transition-all ${t.wrap}`}
      data-testid={`wallet-pkg-${pkg.id}`}
    >
      {badge && (
        <div className={`absolute ${theme === 'green' ? '-top-3 left-1/2 transform -translate-x-1/2' : '-top-3 right-3'}`}>
          <span className={`text-xs font-bold px-3 py-1 rounded-full shadow-md ${
            theme === 'green' ? 'bg-amber-400 text-amber-900' : 'bg-purple-500 text-white'
          }`}>
            {badge}
          </span>
        </div>
      )}
      <div className="text-center">
        <p className={`text-sm font-semibold mb-2 ${t.title}`}>{pkg.name}</p>
        <p className={`text-4xl font-bold mb-2 ${t.number}`}>{pkg.credits}</p>
        <p className={`text-xs mb-3 ${t.sub}`}>credits</p>
        <div className={`rounded-lg py-2 px-3 mb-3 ${t.box}`}>
          <p className="text-2xl font-bold">{pkg.price}</p>
          <p className={`text-xs ${t.sub}`}>{pkg.perCredit} per credit</p>
        </div>
        {saveCopy && (
          <div className={`rounded-lg py-1 px-2 mb-3 text-xs font-bold ${
            theme === 'green' ? 'bg-amber-400/20 text-white' : 'bg-purple-100 text-purple-700'
          }`}>
            {saveCopy}
          </div>
        )}
        <button
          onClick={() => onPurchase(pkg.id)}
          disabled={!!purchasing}
          data-testid={`wallet-purchase-${pkg.id}`}
          className={`w-full font-semibold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 ${t.btn} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isProcessing ? <Loader2 size={14} className="animate-spin" /> : null}
          {isProcessing ? 'Processing...' : ctaLabel}
        </button>
      </div>
    </div>
  );
};

// Public component wrapped in error boundary
const Wallet = () => (
  <ErrorBoundary name="Wallet">
    <WalletInner />
  </ErrorBoundary>
);

export default Wallet;
